import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Mail, ArrowRight, Lock, Music, Layers, Mic } from 'lucide-react';
import { api } from '../app/api';
import { logger } from '../lib/logger';
import { useAuth } from '../app/AuthContext';
import { invalidateLockerRoomEntitlement } from '../hooks/useLockerRoomEntitlement';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useDocumentHead } from './blog/useDocumentHead';

/**
 * LockerRoomWelcome
 * ---------------------------------------------------------------------------
 * Post-Stripe-Checkout success page. The URL is set in the
 * `success_url` of the checkout session created by
 * `/api/billing/locker-room-pass/checkout`:
 *
 *     /locker-room/welcome?session_id={CHECKOUT_SESSION_ID}
 *
 * Responsibilities:
 *   1) Call `/api/billing/locker-room-pass/claim` to:
 *        - verify the checkout with Stripe + provision Pass on the user
 *        - (for guest checkouts) start a session cookie so subsequent
 *          requests carry the user's identity
 *   2) Bust the entitlement cache + refresh the AuthContext so the next
 *      visit to `/locker-room` immediately shows unlocked content.
 *   3) If the user is brand-new (no password yet), surface a clear
 *      "check your email to set a password" notice — that email goes out
 *      automatically as part of the webhook handler.
 *   4) Provide a single CTA to enter the Locker Room.
 *
 * UX safety net:
 *   - Renders a graceful error state if the session_id is missing or the
 *     claim endpoint fails. Users can still navigate back via a link.
 */

export default function LockerRoomWelcome() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh: refreshAuth } = useAuth();
  const [state, setState] = useState({ phase: 'loading', error: null, claim: null });

  const sessionId = params.get('session_id') || '';

  useDocumentHead({
    title: 'Welcome to The Locker Room — Apex Athletic Mind',
    description: 'Your Locker Room Pass is active. Every playlist, the full Apex Six deck, and the rest of the locker room is unlocked.',
    canonical: '/locker-room/welcome',
    robots: 'noindex, nofollow', // transactional page; should not be indexed
  });

  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'instant' });
    if (!sessionId) {
      setState({ phase: 'error', error: 'Missing checkout session reference.', claim: null });
      return;
    }
    let active = true;
    api.post('/api/billing/locker-room-pass/claim', { session_id: sessionId })
      .then((data) => {
        if (!active) return;
        invalidateLockerRoomEntitlement();
        // Refresh AuthContext in the background — for guest checkouts the
        // /claim endpoint set a session cookie; refreshAuth will pick it up.
        refreshAuth().catch((err) => logger.warn('LockerRoomWelcome: refreshAuth failed', err));
        setState({ phase: 'success', error: null, claim: data });
      })
      .catch((err) => {
        if (!active) return;
        logger.error('LockerRoomWelcome: claim failed', err);
        setState({
          phase: 'error',
          error: err?.message || 'We could not verify your checkout.',
          claim: null,
        });
      });
    return () => { active = false; };
  }, [sessionId, refreshAuth]);

  return (
    <main className="min-h-screen bg-[#0B0C0D] text-white pt-24 sm:pt-28 pb-20" data-testid="locker-welcome-root">
      <div className="max-w-[640px] mx-auto px-5 sm:px-8">
        {state.phase === 'loading' && (
          <div className="rounded-3xl bg-[#0F1314] ring-1 ring-white/10 p-8 sm:p-10 shadow-2xl shadow-black/30" data-testid="locker-welcome-loading">
            <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-semibold mb-3">
              Locker Room Pass
            </div>
            <h1 className="font-heading text-[1.75rem] sm:text-[2.1rem] font-bold tracking-tight leading-[1.15] mb-3">
              Activating your Pass…
            </h1>
            <p className="text-[14.5px] text-white/65 leading-snug mb-6 max-w-[44ch]">
              Hold tight while we verify your checkout with Stripe and unlock the locker.
            </p>
            <div className="space-y-3" aria-hidden="true">
              <Skeleton className="h-4 w-3/4 bg-white/5" />
              <Skeleton className="h-4 w-2/3 bg-white/5" />
              <Skeleton className="h-10 w-40 bg-white/5 rounded-xl" />
            </div>
          </div>
        )}

        {state.phase === 'error' && (
          <div className="rounded-3xl bg-[#0F1314] ring-1 ring-white/10 p-8 sm:p-10 shadow-2xl shadow-black/30 text-center" data-testid="locker-welcome-error">
            <div className="mx-auto h-12 w-12 rounded-full bg-red-500/20 ring-1 ring-red-400/30 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-300" />
            </div>
            <h1 className="font-heading text-[1.55rem] sm:text-[1.85rem] font-bold tracking-tight leading-[1.15] mb-3">
              We couldn’t verify your checkout.
            </h1>
            <p className="text-[14px] text-white/65 leading-snug mb-6 max-w-[44ch] mx-auto" data-testid="locker-welcome-error-msg">
              {state.error || 'Something went wrong while activating your Pass.'} If you completed payment with Stripe, your card has not been charged twice — the Pass will auto-activate the moment we sync. Try again in a minute or contact Coach Lewis.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={() => window.location.reload()}
                className="bg-white text-[#0B0C0D] hover:bg-white/90 h-11 px-5 rounded-xl"
                data-testid="locker-welcome-retry"
              >
                Try again
              </Button>
              <Link
                to="/locker-room"
                className="inline-flex items-center justify-center h-11 px-5 rounded-xl bg-white/8 text-white hover:bg-white/12 transition-colors text-[14px] font-semibold"
                data-testid="locker-welcome-back"
              >
                Back to The Locker Room
              </Link>
            </div>
          </div>
        )}

        {state.phase === 'success' && (
          <div className="rounded-3xl bg-gradient-to-br from-[#004C54] via-[#003a40] to-[#0F1314] ring-1 ring-[#4FBCC7]/25 p-8 sm:p-10 shadow-2xl shadow-black/40" data-testid="locker-welcome-success">
            <div className="mx-auto h-14 w-14 rounded-full bg-white text-[#004C54] flex items-center justify-center mb-5">
              <CheckCircle2 className="h-7 w-7" strokeWidth={2.5} />
            </div>
            <div className="text-center">
              <div className="font-mono-accent uppercase tracking-[0.22em] text-[10px] text-[#4FBCC7] font-semibold mb-2">
                Locker Room Pass · Active
              </div>
              <h1 className="font-heading text-[1.8rem] sm:text-[2.25rem] font-bold tracking-tight leading-[1.1] mb-3" data-testid="locker-welcome-title">
                You’re in. Welcome to the locker.
              </h1>
              <p className="text-[15px] text-white/75 leading-snug mb-7 max-w-[44ch] mx-auto">
                Every playlist, the full Apex Six deck, audio drops as they ship, and the monthly Q&amp;A with Coach Lewis — all unlocked.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-7" data-testid="locker-welcome-unlocked">
              {[
                { icon: Music, label: 'All playlists' },
                { icon: Layers, label: 'Apex Six × 6' },
                { icon: Mic, label: 'Weekly drops' },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="rounded-xl bg-black/30 ring-1 ring-white/10 px-3 py-3 text-center">
                    <Icon className="h-4 w-4 text-[#4FBCC7] mx-auto mb-1.5" />
                    <div className="text-[10.5px] sm:text-[11.5px] font-semibold text-white/85 leading-tight">{item.label}</div>
                  </div>
                );
              })}
            </div>

            {state.claim?.needs_password_setup && (
              <div className="rounded-xl bg-black/40 ring-1 ring-white/10 px-4 py-3.5 mb-6 flex items-start gap-3" data-testid="locker-welcome-password-notice">
                <Mail className="h-4 w-4 text-[#4FBCC7] mt-0.5 shrink-0" />
                <div className="text-[12.5px] leading-snug">
                  <div className="font-semibold text-white mb-0.5">Check your inbox</div>
                  <div className="text-white/65">
                    We sent <span className="text-white font-semibold">{state.claim?.email}</span> a link to set a password so you can log back in from any device.
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => navigate('/locker-room')}
                className="flex-1 bg-white text-[#0B0C0D] hover:bg-white/90 h-12 rounded-xl font-semibold text-[14.5px]"
                data-testid="locker-welcome-cta-enter"
              >
                Enter The Locker Room
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
              <Link
                to="/app/dashboard"
                className="inline-flex items-center justify-center h-12 px-4 rounded-xl bg-white/8 text-white hover:bg-white/12 transition-colors text-[13.5px] font-semibold"
                data-testid="locker-welcome-cta-dashboard"
              >
                <Lock className="h-3.5 w-3.5 mr-1.5" />
                Manage subscription
              </Link>
            </div>

            <p className="text-[11px] text-white/40 text-center mt-5 leading-snug">
              Receipt sent by Stripe. Cancel anytime from your dashboard.
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
